/**
 * Coverage contract for an investigation run (NS-34).
 *
 * The backend `complete` event carries `missing_steps` (steps that never ran
 * because the stream budget ran out) and `degraded_steps` (timeout, error,
 * partial, not tested), each with a reason, and keeps a stub
 * `{status, reason, step}` in `all_results` for every step that did not finish.
 * This module is the one place the hook and the report read those shapes.
 */

export interface CoverageStep {
    step: string;
    status?: string;
    reason?: string | null;
}

export interface InvestigationCoverage {
    /** Steps that produced a result (full or partial); null when the backend did not say. */
    steps_completed: number | null;
    missing_steps: CoverageStep[];
    degraded_steps: CoverageStep[];
}

const STUB_STATUSES = new Set(['timeout', 'error', 'skipped']);

/** Backend stub entry for a step that did not finish: `{status, reason, step}` and nothing else. */
export function isCoverageStub(value: unknown): boolean {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const v = value as Record<string, unknown>;
    const keys = Object.keys(v);
    return typeof v.step === 'string'
        && typeof v.status === 'string' && STUB_STATUSES.has(v.status)
        && keys.every((k) => k === 'step' || k === 'status' || k === 'reason');
}

/** `all_results` without the stub entries, so a missing step stays absent for consumers. */
export function stripCoverageStubs(allResults: Record<string, any> | null | undefined): Record<string, any> {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(allResults || {})) {
        if (!isCoverageStub(v)) out[k] = v;
    }
    return out;
}

function toSteps(raw: unknown): CoverageStep[] {
    if (!Array.isArray(raw)) return [];
    const out: CoverageStep[] = [];
    for (const item of raw) {
        if (typeof item === 'string') out.push({ step: item });
        else if (item && typeof item === 'object' && typeof (item as any).step === 'string') {
            const i = item as any;
            out.push({ step: i.step, status: typeof i.status === 'string' ? i.status : undefined, reason: i.reason ?? null });
        }
    }
    return out;
}

/** Read the coverage block of a `complete` payload (or of a saved signal_assessment). */
export function normalizeCoverage(raw: any): InvestigationCoverage | null {
    if (!raw || typeof raw !== 'object') return null;
    if (!('missing_steps' in raw) && !('degraded_steps' in raw) && !('steps_completed' in raw)) return null;
    const sc = Number(raw.steps_completed);
    return {
        steps_completed: Number.isFinite(sc) ? sc : null,
        missing_steps: toSteps(raw.missing_steps),
        degraded_steps: toSteps(raw.degraded_steps),
    };
}

export const STOP_REASON_TEXT: Record<string, string> = {
    time_budget: 'the search time budget ran out',
    limit_reached: 'the 200-address cap was reached',
    depth: 'the hop limit was reached',
    stop_entity: 'it reached an exchange or other stop entity',
    no_entity_coverage: 'the addresses reached had no entity coverage',
    no_inbound_transactions: 'the wallet shows no inbound transactions in the window read',
    no_outbound_transactions: 'the wallet shows no outbound transactions in the window read',
    root_page_unavailable: "the subject's transactions could not be read, so the trace did not run",
    complete: 'the search space was exhausted',
};

export function stopReasonText(reason: unknown): string {
    const r = String(reason ?? '');
    return STOP_REASON_TEXT[r] || r.replace(/_/g, ' ');
}

/** One plain sentence for a step that did not finish normally. */
export function plainStepReason(status: string | undefined, reason: string | null | undefined): string {
    const r = (reason || '').trim();
    switch (status) {
        case 'timeout': return r ? `Timed out: ${r}` : 'Timed out before it returned a result';
        case 'skipped': return r ? `Did not run: ${r}` : 'Did not run (the investigation ran out of time)';
        case 'error': return r ? `Failed: ${r}` : 'Failed';
        case 'unavailable': return r ? `Could not be tested: ${r}` : 'Could not be tested';
        case 'partial': return r ? `Partial result: ${r}` : 'Partial result';
        default: return r || 'Did not finish normally';
    }
}

/** Step keys to the labels the report and the card use. */
export const COVERAGE_STEP_LABELS: Record<string, string> = {
    exchange_detection: 'Exchange Detection',
    velocity: 'Velocity Profile',
    counterparty_triage: 'Top Counterparties',
    counterparty_enrichment: 'Counterparty Enrichment',
    cex_offramp: 'CEX Off-ramp Correlation',
    adaptive_transactions: 'Adaptive Transactions',
    multi_hop_exposure: 'Multi-Hop Exposure',
    backward_trace: 'Backward Trace',
    forward_trace: 'Forward Trace',
    compliant_exchange: 'Compliant Exchange Paths',
    circulation: 'Circulation and Pass-Through',
    signal_assessment: 'Signal Assessment',
};

export function coverageStepLabel(step: string): string {
    return COVERAGE_STEP_LABELS[step] || step.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
